export type SocketAuth = {
  token: string;
  sessionId: string;
};

export const enum ObjectType {
  NONE = '',

  //CLIENT ONLY
  TOOLTIP = 'tooltip',
  ANCHOR = 'anchor',
  SPACE = 'space',
  ROOM = 'room',
  BASE = 'base',
  CONTAINER = 'container',
  CURSOR = 'cursor',
  IMAGE = 'image', //on client: imageEnt
  PARTICLE_SYSTEM = 'particle_system',
  DOM_ELEMENT = 'dom_element',
  USER_TEXT_EDITOR = 'textObjectEditor',
  TEXT_EDITOR = 'textEditor',
  ICON_BUTTON = 'icon_button',
  PROGRESS_METER = 'progress_meter',
  OTHER_CLIENT = 'other_client',
  SLIDER = 'slider',
  AUDIO_OBJECT = 'audio_object',
  QRCODE = 'qrcode',
  SOE_ITEM = 'soe_item',
  CONNECTION = 'connection',
  CONTENT_BUBBLE = 'content_bubble',
  AUDIO_INDICATOR = 'audio_indicator',
  TEXTOBJECT_SIMPLE = 'textobject_simple',

  //SERVER & CLIENT
  LOBBY = 'lobby',
  CMS_CONSOLE = 'cms_console',
  VIDEOCALL = 'videocall',
  VIDEOCALL_CUSTOM = 'videocall_custom',
  CHAT = 'chat',

  //SERVER & CLIENT & CMS
  CANVAS_OLD = 'canvas2',
  CANVAS = 'canvas',
  STEP_AREA = 'step_area',
  IMAGE_OBJECT = 'image_object',
  IMAGE_PICKER = 'image_picker',
  PROFILE_NODE = 'profile_node',
  ROOM_NODE = 'room_node',
  SOCIAL_ROOM_NODE = 'social_room_node',
  LIVE_SESSION_NODE = 'live_session_node',
  JOURNEY_START_NODE = 'journey_start_node',
  JOURNEY_NODE = 'journey_node',
  TEXTOBJECT = 'textobject',
  TEXTOBJECT2 = 'textobject2',
  USER_TEXT = 'usertext',
  VIDEOPLAYER = 'video',
  TEXT_BUTTON = 'button',
  DROPAREA = 'droparea',
  TIMER = 'timer',
  INFOGRAPHIC = 'infographic',
  SLIDER_QUESTION = 'question',
  PAGED_TEXT = 'paged_text',
  READYCHECK = 'readycheck',
  // LINK = 'link',
  // AVATAR_VISUAL = 'avatar_visual',
  INSTRUCTION_BAR = 'instruction_bar',
  // LIST_VIEW = 'gallery_view',
  QUOTE = 'quote',
  // RESTVALUES = 'rest_values',
  REFRAME = 'reframe',
  // LEADERSHIP_OBJECT = 'leadership_object',
  RESILIENCE_CANVAS = 'resilience_canvas',
  NAV_NODE = 'navigation_node',
  // GALLERY_NODE = 'gallery_node', //depricated?
  SOE_ITEM_CANVAS = 'soe_item_canvas',
  BREAKOUT_CONTROLLER = 'breakout_controller',
  BREAKOUT_LOBBY_INFO = 'breakout_lobby_info',
  JOURNEY_VIEW = 'journey_view',
  STEP_INSTRUCTION = 'step_instruction',
  SHAPE = 'shape',
  TEXT_INPUT = 'text_input',
  STEP_CONTROLLER = 'step_controller',

  //SERVER & CMS
  AI_CONTROLLER = 'ai_controller',
  IMAGE_UPLOAD_CONTROLLER = 'image_upload_controller',
  SEQUENCE_DATA_CONTROLLER = 'sequence_data_controller',
  SOE_CONTROLLER = 'soe_controller',
  USER_METRICS_CONTROLLER = 'user_metrics_controller',
}

export const enum ShapeType {
  RECTANGLE = 'rectangle',
  CIRCLE = 'circle',
  LINE = 'line',
}

export const enum ShapePresetStyle {
  WHITE_BORDER = 'white_border',
  WHITE_FILLED = 'white_filled',
  CUSTOM = 'custom',
}

export const enum LineEndpointType {
  NONE = 'none',
  CLOSED_ARROW = 'closed_arrow',
  OPEN_ARROW = 'open_arrow',
  DOT = 'dot',
}

const IMAGE_SIZES = [
  '',
  'small',
  'medium',
  'large',
  'thumb',
  'small_blurred',
  'medium_blurred',
  'large_blurred',
  'thumb_blurred',
] as const;

export type ImageSize = (typeof IMAGE_SIZES)[number];

export enum RoomNavigationType {
  NONE = '',
  HIDE = 'hide',
  BACK = 'back',
  QUIT = 'quit',
  LEAVE_SOCIAL = 'leave_social', //with videocall but without leave conformation modal
  LEAVE_MISSION = 'leave_mission', //with videocall and with leave conformation modal
  LEAVE_FINISHED_MISSION = 'leave_finished_mission', //with videocall and with leave conformation modal
}

export enum Alignment_Horizontal {
  NONE = '',
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum Alignment_Vertical {
  NONE = '',
  CENTER = 'center',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export enum Side {
  NONE = '',
  LEFT = 'left',
  RIGHT = 'right',
  ABOVE = 'above',
  BELOW = 'below',
}

export enum TimerState {
  NONE = '',
  NOT_STARTED = 'not_started',
  PRE_START = 'pre_start',
  PAUSED = 'paused',
  COUNTING = 'counting',
  IN_OVERTIME = 'in_overtime',
  OVERTIME_PASSED = 'overtime_passed',
}

export enum RoomNodeStyle {
  NONE = '',
  DEFAULT = 'default',
  BIG = 'big',
  CIRCLE = 'circle',
}

export enum VIDEOCALL_LAYOUT {
  NONE = '',
  PRE_JOIN = 'prejoin',
  CANVAS = 'canvas',
  HIDDEN = 'hidden',
  RIGHT = 'right',
  RIGHT_SPEAKER = 'right_speaker',
  FULL_SCREEN = 'fullscreen',
  MULTI_USER = 'multi_user',
  LOBBY = 'lobby',
  MULTI_USER_HOSTS_RIGHT = 'multiuser_hosts_right',
}

export enum CANVAS_SPEED {
  NONE = '',
  DEFAULT = 'default',
  INSTANT = 'instant',
  FAST = 'fast',
  SLOW = 'slow',
}

export enum BreakoutGroupStatus {
  NONE = '',
  NOT_READY = 'not_ready',
  READY = 'not_ready',
  START_LOBBY = 'start_lobby',
  LOBBY = 'lobby',
  ACTIVE_IN_STEP = 'active_in_step',
  FINISHED = 'finished',
  EMPTY = 'empty',
}

export enum BreakoutClientStatus {
  NONE = '',
  IN_PLENARY = 'in_plenary',
  IN_PLENARY_JOINING = 'in_plenary_joining',
  LEFT = 'left',
  IN_BREAKOUT_ROOM = 'in_breakout_room',
  IN_BREAKOUT_ROOM_JOINING = 'in_breakout_room_joining',
  OFFLINE = 'offline',
  SOFT_DISCONNECTED = 'soft_disconnected',
  DISCONNECTED = 'disconnected',
  NOT_READY = 'not_ready',
}

export type ResilienceCanvasField = 'body' | 'mind' | 'soul' | 'heart';

type JSONProperty = [string, PropertyType] | [string, PropertyType, true];

type PropertyType =
  | 'string'
  | 'number'
  | 'bigint'
  | 'boolean'
  | 'symbol'
  | 'undefined'
  | 'object'
  | 'function'
  | 'EntityProps';
const isRequired = true;

export enum NavigationType {
  BACK = '_back',
  CLOSE = '_close',
  HOME = '_home',
  SEQUENCE = '_seq',
  RELOAD = '_reload',
}

export enum SoeItemType {
  NONE = '',
  STRENGTH = 'strength',
  VALUE = 'value',
  PASSION = 'passion',
}

export enum LiveSessionState {
  NONE = '',
  NOT_PLANNED = 'not_planned',
  PLANNED = 'planned',
  UP_NEXT = 'up_next',
  OPEN = 'pre-open',
  CONTINUE = 'continue',
  ALWAYS_OPEN = 'always-open',
  FULL = 'full', //the mission is acive, and the max amount of users is reached
  STARTED = 'open', //the mission is active
  FINISHED = 'finished', //the user has finished this specific session
  COMPLETED = 'completed',
}

export enum BreakoutLobbyState {
  NONE = '',
  PRE_OPEN = 'pre-open',
  MATCHING = 'matching',
  CLOSED = 'closed',
  CLOSED_PROBLEM = 'problem',
}

export enum StepControllerState {
  NONE = '',
  HIDE = 'hide',
  NOT_READY = 'not_ready',
  MOVE_FORWARD = 'move_forward',
  FINISH_MISSION = 'finish_mission',
}

export type CanvasFocus = {
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  speed: CANVAS_SPEED;
};

export type ObjectEventTuple<T extends keyof ObjectTypeMap> =
  | ObjectEventTupleFull<T>
  | ObjectEventTuplePartial<T>;

export type ObjectEventTupleFull<T extends keyof ObjectTypeMap> = [
  objectType: T,
  sId: number,
  full: true,
  roomId: string,
  props: ObjectTypeMap[T],
];

export type ObjectEventTuplePartial<T extends keyof ObjectTypeMap> = [
  objectType: T,
  sId: number,
  full: false,
  props: Partial<ObjectTypeMap[T]>,
];

export type AnyObjectEventProps = {
  [K in keyof ObjectTypeMap]: ObjectTypeMap[K];
}[keyof ObjectTypeMap];

type ObjectTypeMap = {
  room: ObjectSyncData_Room;
  button: ObjectSyncData_Timer;
  breakout_lobby_info: ObjectSyncData_BreakoutLobbyInfo;
};

// This creates a union of:
export type AnySocketEvent = {
  [K in keyof ObjectTypeMap]: ObjectEventTuple<K>;
}[keyof ObjectTypeMap];

export type ObjectSyncData =
  | ObjectSyncData_Room
  | ObjectSyncData_BreakoutLobbyInfo
  | ObjectSyncData_JourneyView;

export type ObjectEventData =
  | ServerEvent_Room
  | ServerEvent_ProfileNode
  | ServerEvent_Timer
  | ServerEvent_InstructionBar
  | ServerEvent_StepArea
  | ServerEvent_VideoCall
  | ServerEvent_VideoPlayer
  | ServerEvent_TextObject
  | ServerEvent_UserText
  | ServerEvent_Canvas
  | ServerEvent_DropArea
  | ServerEvent_PagedText
  | ServerEvent_RoomNode
  | ServerEvent_SocialRoomNode
  | ServerEvent_Chat
  | ServerEvent_QuoteObject
  | ServerEvent_LiveSessionNode
  | ServerEvent_JourneyView
  | ServerEvent_NavigationNode
  | ServerEvent_ImagePicker
  | ServerEvent_ContentBubble
  | ServerEvent_CmsConsole
  | ServerEvent_ReadyCheck
  | ServerEvent_Lobby
  | ServerEvent_TextButton
  | ServerEvent_Question
  | ServerEvent_ReframeObject
  | ServerEvent_ResilienceCanvas
  | ServerEvent_ImageObject
  | ServerEvent_InfoGraphic
  | ServerEvent_BreakoutController
  | ServerEvent_BreakoutLobbyInfo
  | ServerEvent_SoeItemCanvas
  | ServerEvent_StepInstruction
  | ServerEvent_Shape
  | ServerEvent_TextInput
  | ServerEvent_TextObject2
  | ServerEvent_StepController;

export type ServerEvent_ProfileNode = {
  objectType: ObjectType.PROFILE_NODE;
  sId: number;
  roomId?: string;
  full?: boolean;
  entityProps?: ServerEvent_EntityProps;
  imageUri?: string;
  firstName?: string;
  lastName?: string;
  sequenceSlug?: string;
  allowEdit?: boolean;
  imageUploadSuccess?: boolean;
  infoWhenNoImage?: string;
};

export type ObjectSyncData_JourneyView = {
  entityProps?: ServerEvent_EntityProps;
  prepareItems: JourneyPrepareItemData[];
  elements: JourneyElement[];
  showJourneyIntro?: boolean;
  showOpenMissionTip?: boolean;
};

export type JourneyPrepareItemData = {
  label: string;
  state: '' | 'done' | 'prio';
};

export type JourneyElement = {
  index: number;
  type: '' | 'simple_node' | 'live_session';
  serverObjId: number;
  positionX: number;
  positionY: number;
  label: string;
};

export type ServerEvent_JourneyView = {
  objectType: ObjectType.JOURNEY_VIEW;
  sId: number;
  roomId?: string;
  full?: boolean;
} & ObjectSyncData_JourneyView;

export type ObjectSyncData_Timer = {
  // objectType: ObjectType.TIMER;
  // sId: number;
  // roomId?: string;
  // full?: boolean;
  entityProps?: ServerEvent_EntityProps;
  currentTime?: number;
  startTime?: number;
  state?: string;

  showContinueButton?: boolean; //when true, the timer waits for user conformation before coninuing
};

export type ServerEvent_Timer = {
  objectType: ObjectType.TIMER;
  sId: number;
  roomId?: string;
  full?: boolean;
  entityProps?: ServerEvent_EntityProps;
  currentTime?: number;
  startTime?: number;
  state?: TimerState;
  // message?: string;
  // readyMessage?: string;
  showContinueButton?: boolean; //when true, the timer waits for user conformation before coninuing
};

export type ServerEvent_InstructionBar = {
  objectType: ObjectType.INSTRUCTION_BAR;
  sId: number;
  roomId?: string;
  full?: boolean;
  entityProps?: ServerEvent_EntityProps;
  heightEm?: number;
  preparation?: string[];
  tips?: InstructionTip[];
  instruction?: string;
  allClientsPrepared?: boolean;
  notReadyMessage?: string;
  overtimePassedMessage?: string;
};

export type InstructionTip = {
  title: string;
  body: string;
};

export type ServerEvent_VideoCall = {
  objectType: ObjectType.VIDEOCALL;
  sId: number;
  roomId?: string;
  full?: boolean;
  state?: '' | 'local_only' | 'room_conference';
  entityProps?: ServerEvent_EntityProps;
  JWT?: string;
  preJoinState?: '' | 'join_lobby' | 'join_mission';
  roomName?: string;
  layout?: VIDEOCALL_LAYOUT;
  allowScreenShare?: boolean;
  skipDeviceCheck?: boolean;
};

export type ServerEvent_VideoPlayer = {
  objectType: ObjectType.VIDEOPLAYER;
  sId: number;
  roomId?: string;
  full?: boolean;
  entityProps?: ServerEvent_EntityProps;
  sizePx?: number;
  style?: string;
  uri?: string;
  imageUri?: string;
  captionsUri?: string;
  name: string;
  title: string;
  readyToPlay?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  fixedSize?: boolean;
  currentState?: string;
  currentTime?: number;
  showSubtitles?: boolean;
  showSyncVideoTip?: boolean;
};

export type ServerEvent_TextObject2 = {
  objectType: ObjectType.TEXTOBJECT2;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  text?: string;
  width?: number;
  height?: number;
  wrapText?: boolean;
};

export type ServerEvent_TextObject = {
  objectType: ObjectType.TEXTOBJECT;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  text?: string[];
  widthEm?: number;
  align?: Alignment_Horizontal;
  audio?: string;
  style?: string;
  fixedFontSize?: boolean;
};

export type ServerEvent_UserText = {
  objectType: ObjectType.USER_TEXT;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  clientId?: number;
  full?: boolean;
  text?: string;
  isLocked?: boolean;
  lockedMsg?: string;
  isTextInput?: boolean;
};

export type ServerEvent_Canvas = {
  objectType: ObjectType.CANVAS;

  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  relativeScreenFocusPointX?: number;
  relativeScreenFocusPointY?: number;
  focusX?: number;
  focusY?: number;
  focusW?: number;
  focusH?: number;
  focusSpeed?: CANVAS_SPEED;
  refocusRequest?: boolean;
  enableRefocus?: boolean;
  showRefocusTip?: boolean;
  showNavigationTip?: boolean;
};

export type ServerEvent_Brainsnack = {
  partId: string;
  body: string;
  title: string;
  videoUrl?: string;
};

export type ServerEvent_BreakoutLobbyInfo = {
  objectType: ObjectType.BREAKOUT_LOBBY_INFO;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
} & ObjectSyncData_BreakoutLobbyInfo;

export type ObjectSyncData_BreakoutLobbyInfo = {
  // objectType: ObjectType.BREAKOUT_CONTROLLER;
  // sId: number;
  // roomId?: string;
  startInSec?: number;
  entityProps?: ServerEvent_EntityProps;
  // full?: boolean;
  state: BreakoutLobbyState;
  textKey: string;
  textVars?: Record<string, string>;
  nextSessions: number[];
};

export enum InstructionType {
  NONE = '',
  DISCUSS = 'discuss',
  REFLECT = 'reflect',
  LISTEN = 'listen',
  SHARE = 'share',
  INPUT = 'input',
  READ = 'read',
}

export type ServerEvent_StepInstruction = {
  objectType: ObjectType.STEP_INSTRUCTION;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  instructionText?: string;
  introText?: string;
  instructionType?: InstructionType;
};

export type ServerEvent_StepController = {
  objectType: ObjectType.STEP_CONTROLLER;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  state?: StepControllerState;
  othersText?: string;
  timeTotalSec?: number;
  timeLeftSec?: number;
  readyText?: string;
  isReady?: boolean;
  readyCount?: number;
  readyNeeded?: number;
  focus?: CanvasFocus;
  label?: string;
  showTimerTip?: boolean;
};

export type ServerEvent_Shape = {
  objectType: ObjectType.SHAPE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  shapeType?: ShapeType;
  style?: ShapePresetStyle;
  width?: number;
  height?: number;
  cornerRadius?: number;
  radius?: number;
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  styleFillColor?: string;
  styleLineWeight?: number;
  styleLineColor?: string;
  lineStartType?: LineEndpointType;
  lineEndType?: LineEndpointType;
};

export type ServerEvent_TextInput = {
  objectType: ObjectType.TEXT_INPUT;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  width?: number;
  height?: number;
  maxLength?: number;
  isPrivate?: boolean;
  locked?: boolean;
  value?: string;
};

export type ServerEvent_BreakoutController = {
  objectType: ObjectType.BREAKOUT_CONTROLLER;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  stickToScreen?: boolean;
  sequenceSlug?: string;
  totalSteps?: number;
  lobbyState?: BreakoutLobbyState;
  info?: string;
  groupData?: BreakoutGroupData[];
  readyGroupInfo?: { count: number; users: number };
  startedGroupInfo?: { count: number; users: number };
  notStartedGroupInfo?: { count: number; users: number };
  emptyGroupInfo?: { count: number; users: number };
  finishedGroupInfo?: { count: number; users: number };
  clientsWithoutGroup?: BreakoutClientData[];

  stopTimer?: number;
  stepCount?: number;
};

export type BreakoutGroupData = {
  groupId: number;
  userGroupName: string;
  started: boolean; //is there is a room created
  isReady: boolean; //if a room could be created
  isFinished: boolean; //if the breakout mission is set to completed
  statusString: string;
  status: BreakoutGroupStatus;
  canJoin: boolean; //if the host could join
  canLeave: boolean; //if the host could leave
  clients: BreakoutClientData[];
  activeStep: number;
  activeRound: number;
};

export type BreakoutClientData = {
  id: number;
  name: string;
  status: BreakoutClientStatus;
  isYou: boolean;
};

export type ServerEvent_DropArea = {
  objectType: ObjectType.DROPAREA;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  width?: number;
  height?: number;
  enableDirectInput?: boolean;
  label?: string;
  attachedEntityId?: number;
  acceptObjectTypes?: ObjectType[];
  characterLimit?: number;
  showCharacterLimit?: number;
  showLabelOnAttach?: boolean;
  labelAlignment?: Alignment_Horizontal;
  typeHintOnHover?: boolean;
};

export type ServerEvent_EntityProps = {
  parSId?: number;
  X?: number;
  Y?: number;
  relX?: number;
  relY?: number;
  pivX?: number;
  pivY?: number;
  moveMode?: string;
  style?: string;

  connections?: EntityConnectionData[];
  noAutoSize?: boolean;
};

export type EntityConnectionData = {
  entityActionId?: string;
  entityServerObjId: number;
  style: string;
  myAttachpoints: string;
  otherAttachpoints: string;
  myLineEndPointType: LineEndpointType;
  otherLineEndPointType: LineEndpointType;
};

export type ServerEvent_PagedText = {
  objectType: ObjectType.PAGED_TEXT;

  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  text?: string[];
  title?: string;
  audio?: string[];
  hidden?: boolean;
  widthEm?: number;
};

export type ServerEvent_Chat = {
  objectType: ObjectType.CHAT;

  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;

  userData?: ChatUserData[];
  lastReadMessageIndex?: number;
  messageData?: ChatMessageData[];
};

export type ChatUserData = {
  participantId: number;
  firstName: string;
  lastName: string;
  isYou: boolean;
};

export type ChatMessageData = {
  participantId: number;
  msgIndex: number;
  msg: string;
  dateTime: number;
};

export type ServerEvent_StepArea = {
  objectType: ObjectType.STEP_AREA;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  label?: string;
  size?: number;
};

export type ServerEvent_RoomNode = {
  objectType: ObjectType.ROOM_NODE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  roomContentId?: string;
  label?: string;
  locked?: boolean;
  infoMsg?: string;
  iconId?: string;
  style?: RoomNodeStyle;
};

export type ServerEvent_SocialRoomNode = {
  objectType: ObjectType.SOCIAL_ROOM_NODE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  roomContentId?: string;
  label?: string;
  info?: string;
  iconId?: string;
  clientCount?: number;
  maxClients?: number;
  isHost?: boolean;
  dateTime?: number;
  dateTimeText?: string;
  isOpen?: boolean;
  durationMinutes?: number;
};

export type ServerEvent_LiveSessionNode = {
  objectType: ObjectType.LIVE_SESSION_NODE | ObjectType.JOURNEY_NODE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  label?: string;
  info?: string;
  imageUri?: string;
  clientCount?: number;
  isHost?: boolean;
  forceCET?: boolean;
  sessionData?: LiveSessionData[];
  journeySide?: '' | 'right-side' | 'left-side';
  isFirst?: boolean;
  linkedToSId?: number;
  history?: { started: number; finished: boolean }[];
};

export type LiveSessionData = {
  startDate: number;
  info: string;
  state: LiveSessionState;
  slug: string;
};

export type ServerEvent_QuoteObject = {
  objectType: ObjectType.QUOTE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  quote: string;
  author: string;
  imageUri: string;
};

export type ServerEvent_ReframeObject = {
  objectType: ObjectType.REFRAME;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  reframe?: Reframe;
};

export type ServerEvent_ContentBubble = {
  objectType: ObjectType.CONTENT_BUBBLE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  size?: number;
};

export type ServerEvent_ResilienceCanvas = {
  objectType: ObjectType.RESILIENCE_CANVAS;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  minimized?: boolean;
  editMode?: boolean;
  userName?: string;
  body?: string[];
  soul?: string[];
  mind?: string[];
  heart?: string[];
};

export type Reframe = {
  negativeCoreBelief: string;
  newCoreBelief: string;
  supportingBeliefs: string[];
  oppositeBeliefs: string[];
};

export type ServerEvent_NavigationNode = {
  objectType: ObjectType.NAV_NODE;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  direction?: number;
  type?: 'start' | 'end';
  othersText?: string;
  timeTotalSec?: number;
  timeLeftSec?: number;
  readyText?: string;
  isReady?: boolean;
  readyCount?: number;
  readyNeeded?: number;
  focus?: CanvasFocus;
  label?: string;
  showTimerTip?: boolean;
};

export type ServerEvent_Base = {
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
};

export type ServerEvent_CmsConsole = ServerEvent_Base & {
  objectType: ObjectType.CMS_CONSOLE;
  sequenceSlug?: string;
  roomInstandeId?: string;
  sequenceTitle?: string;
  stepName?: string;
  nextStepNames?: string[];
  sequenceState?: string;
  debugMsg?: string;
  roomDataValues?: SyncedRoomParam[];
};

export type SyncedRoomParam = { label: string; value: string | number };

export type ServerEvent_ReadyCheck = {
  objectType: ObjectType.READYCHECK;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  label?: string;
  othersText?: string;
  readyText?: string;
  showButton?: boolean;
  isReady?: boolean;
};

export type ServerEvent_Lobby = {
  objectType: ObjectType.LOBBY;
  sId: number;
  roomId: string;
  full: boolean;
  title?: string;
  users?: LobbyUser[];
  info?: string;
  infoRequirement?: string;
  infoRequirementVars?: Record<string, string>;
  infoWaitingFor?: string;
  infoProblem?: string;
  showReadyButton?: boolean;
  userIsReady?: boolean;
  btnLabel?: string;
  showSupportTip?: boolean;
};

export type LobbyUser = {
  name: string;
  profilePicUri: string;
  participantStatus: ParticipantRoomStatus;
  slug: string;
  reservationUserId: string;
};

export enum ParticipantRoomStatus {
  NONE = 0,
  READY,
  PRESENT,
  JOINING_ROOM,
  DISCONNECTED,
  NOT_JOINED,
}

export type ServerEvent_TextButton = {
  objectType: ObjectType.TEXT_BUTTON;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  label?: string;
  iconId?: string;
  buttonAction?: string;
  buttonActionValue?: string;
  showButton?: boolean;
  showLoader?: boolean;
  hintText?: string;
  hintPosition?: '' | 'bottom' | 'top' | 'left' | 'right';
  minWidthEm?: number;
};

export type ServerEvent_ImageObject = {
  objectType: ObjectType.IMAGE_OBJECT;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  label?: string;
  imageUri?: string;
  clientImageId?: string;
  maxImageWidthEm?: number;
  minImageWidthEm?: number;
  maxImageHeightEm?: number;
  backgroundHexColor?: string;
  appearImmediately?: boolean;
  noHover?: boolean;
  fixedSize?: boolean;
};

export type ServerEvent_InfoGraphic = {
  objectType: ObjectType.INFOGRAPHIC;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  label?: string;
  imageUri?: string;
  clientImageId?: string;
  widthEm?: number;
  textWidthEm?: number;
  defaultText?: string;
  defaultTextStyle?: string;
  textAlign?: Alignment_Horizontal;
  textPlacement?: Side;
  backgroundHexColor?: string;
  noHover?: boolean;
  hotspots?: InfoGraphicHotspotData[];
  horTextOffsetEm?: number;
  vertTextOffsetEm?: number;
  boxedText?: boolean;
  showBorder?: boolean;
};

export type InfoGraphicHotspotData = {
  shape: 'circle' | 'rectangle';
  relativePositionX: number;
  relativePositionY: number;
  relativeRadius: number;
  relativeWidth: number;
  relativeHeight: number;
  text: string;
  textPlacement: InfoGraphicTextPlacement;
  horTextOffsetPx: number;
  vertTextOffsetPx: number;
  textStyle: string;
};

export enum InfoGraphicTextPlacement {
  DEFAULT = '',
  BELOW_HOTSPOT = 'below_hotspot',
  ABOVE_HOTSPOT = 'above_hotspot',
  LEFT_OF_HOTSPOT = 'left_of_hotspot',
  RIGHT_OF_HOTSPOT = 'right_of_hotspot',
}

export type ServerEvent_SoeItemCanvas = {
  objectType: ObjectType.SOE_ITEM_CANVAS;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
  type?: SoeItemType;
  items?: SoeItemData[];
  compactView?: boolean;
  titleText?: string;
};

export type SoeItemData = {
  id: number;
  type: SoeItemType;
  text: string;
  prio: number; //top 3 is 0, 1 & 2
};

export type ServerEvent_ImagePicker = {
  objectType: ObjectType.IMAGE_PICKER;
  sId: number;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  full?: boolean;
};

export enum QuestionState {
  NONE = '',
  OPEN_SOLO = 'open_solo',
  OPEN_MULTI = 'open_multi',
  LOCKED_SOLO = 'locked_solo',
  LOCKED_MULTI = 'locked_multi',
  LOCKED_AND_STORE = 'locked_and_store',
}

export type ServerEvent_Question = {
  objectType: ObjectType.SLIDER_QUESTION;
  sId: number;
  full?: boolean;
  roomId?: string;
  entityProps?: ServerEvent_EntityProps;
  text?: string;
  labelLeft?: string;
  labelRight?: string;
  colorLeft?: string;
  colorRight?: string;
  allowAnswer?: boolean;
  readyMessage?: string;
  startValue?: number;
  clientAnswers?: ClientAnswer[];
  autoApply?: boolean;
  applyLabel?: string;
  minValue?: number;
  maxValue?: number;
  snapToInt?: boolean;
  length?: number;
  locked?: boolean;
  labels?: string[];
  showCurrentLabel?: boolean;
};

export type ClientAnswer = {
  value: number;
  img_uri: string;
  name: string;
};

export type ServerEvent_Room = {
  objectType: ObjectType.ROOM;
  sId: number;
  full?: boolean;
  roomId: string;
  name?: string;
  joined?: boolean;
  userIsHost?: boolean;
  message?: string;
  JWT?: string;
  userInfo?: RoomUserInfo[]; //with more than 6 users, no user-info is send
  roomUserCount?: number;
  syncCursor?: boolean;
  showCursor?: boolean;
  canvasId?: number;
  syncCursorText?: boolean;
  syncUserText?: boolean;
  navigationType?: RoomNavigationType;
  quitOptions?: string[];
  openVideoCallReadyCheck?: boolean;
  videoCallSId?: number;
};

export type ObjectSyncData_Room = {
  // objectType: ObjectType.ROOM;
  // sId: number;
  // full?: boolean;
  // roomId: string;
  name?: string;
  joined?: boolean;
  userIsHost?: boolean;
  message?: string;
  JWT?: string;
  userInfo?: RoomUserInfo[]; //with more than 6 users, no user-info is send
  roomUserCount?: number;
  syncCursor?: boolean;
  showCursor?: boolean;
  canvasId?: number;
  syncCursorText?: boolean;
  syncUserText?: boolean;
  navigationType?: RoomNavigationType;
  quitOptions?: string[];
  openVideoCallReadyCheck?: boolean;
  videoCallSId?: number;
};

export type RoomUserInfo = {
  firstname: string;
  lastname: string;
  slug: string;
  profileUriThumb: string;
  profileUriMedium: string;
  roomUserIndex: number;
  isReservedUser: boolean;
  justLeft: boolean;
  left: boolean;
  isHost: boolean;
  isNew: boolean;
  disconnected: boolean;
};

export type ImageSizes = {
  thumb: ImageDetails;
  small: ImageDetails;
  medium: ImageDetails;
  large: ImageDetails;
};

export type ImageDetails = {
  width: number;
  height: number;
  mimetype: string;
  uri: string;
  filesize: number;
};

export type ServerEvent_ClientInfo = {
  firstName: string;
  lastName: string;
  email: string;
  slug: string;
  profilePicSizes: ImageSizes;
  debugRole?: boolean;
  serverVersion?: string;
  intercomToken?: string;
};

export type ServerEvent_RequestImageUpload = {
  sId: number;
  title: string;
  alt: string;
};

export enum SharedRoomDisplayStyle {
  NONE = 'none',
  MAIN = 'main',
  OVERLAY = 'overlay',
  PROFILE = 'profile',
}

export type JSON_VideoCallSuccessState = {
  roomId: string;
  state: string;
  sId: number;
};

export type JSON_BreakoutGroupAction = {
  action: string;
  sId: number;
  groupId: number;
  baClientId?: number;
};

export type JSON_VideoCallJoinedState = {
  sId: number;
  joined: boolean;
  error?: boolean;
  participantId?: string;
};

export type JSON_ClientReady = {
  serverSession: string;
  lastEmitNr: number;
  sequence: string;
  data: string[];
};

export type JSON_ImageUploadResponse = {
  sId: number;
  objectType: ObjectType;
  uploadedImageId: string;
  uploadedImageUrl: string;
};

export type JSON_ServerDebugCommand = {
  commandType: string;
  data?: string[];
};

export type JSON_RequestNavigation = {
  currentRoomId: string;
  navType: NavigationType;
  slug?: string;
};

export type JSON_VideocallPrepReady = {
  roomId: string;
  ready: boolean;
};

export type ServerEvent_RoomTerminated = {
  roomId: string;
  msg: string;
};

export type JSON_SetBrainsnackRead = {
  moduleSlug: string;
  partId: string;
  // journeySlug: string;
};

export type JSON_UserEmit = {
  a: [number, number] | [number, number, 0 | 1] | [number, number, 0 | 1, string];
};

export type JSON_SupportWidgetOpened = {
  source: 'user' | 'videocall_more_info' | 'service_health_contact_support' | 'system';
};

export type JSON_VideoSyncAction = {
  sId: number;
  action: string;
  time: number;
  msg?: string;
  msgVars?: Record<string, string>;
};

export type JSON_DevicesChecked = {
  checked: boolean;
};

export type JSON_UpdateAccessToken = {
  token: string;
};

export type JSON_SetSubtitlesPreference = {
  show: boolean;
};

export type JSON_SetUserFlag = {
  flag: string;
  value: boolean;
};

export type JSON_StartJourneyPrepareItem = {
  sId: number;
  itemIndex: number;
};

export type JSON_TextInputValueChange = {
  sId: number;
  value: string;
};

export type JSON_ChatMessage = {
  sId: number;
  msg: string;
};

export type JSON_ChatMessageRead = {
  sId: number;
  msgIndex: number;
};

export type JSON_ReserveRoom = {
  sequenceSlug: string;
  userIds: string[];
};

export type JSON_UpdateUserText = {
  clientId: number;
  text: string;
  positionX: number;
  positionY: number;
  roomId: string;
  isAttached: boolean;
};

export type JSON_UserTextChange = {
  sId: number;
  text?: string;
  posX?: number;
  posY?: number;
  locked?: boolean;
};

export type JSON_UserObjectChange = {
  sId: number;
  posX?: number;
  posY?: number;
  locked?: boolean;
};

export type JSON_OpenRoomNode = {
  roomNodeObjId: number;
  type?: string;
};

export type JSON_JoinLiveSession = {
  objId: number;
  sessionSlug: string;
  forceTestHost?: boolean;
  forceTestUser?: boolean;
};

export type JSON_OpenGroupRoom = Record<string, never>;

export type JSON_SetObjectState = {
  sId: number;
  objectType: ObjectType;
  state: string;
};

export type JSON_DropAreaDetachEntity = {
  dropAreaServerId: number;
};

export type JSON_RemoveObject = {
  sId: number;
  roomId: string;
  objectType: ObjectType;
};

export type JSON_DropAreaAttachEntity = {
  dropAreaServerId: number;
  entityServerId: number;
  userId: string;
};

export type JSON_QuestionAnswer = {
  questionServerId: number;
  answerClientId?: number;
  answerValue: number;
  positionX?: number;
  positionY?: number;
};

export type JSON_CmsConsole = {
  sId: number;
  debug_to_step?: string;
};

export type JSON_SyncSoeItem = {
  type: SoeItemType;
  id: number;
  text: string;
  prio: number;
  doRemove?: boolean;
};

export type JSON_SyncResilienceCanvasPart = {
  sId: number;
  partId: string; //body,mind, soul, heart
  words: string[];
};

export type JSON_ClientLocksText = {
  entityServerId: number;
  locked: boolean;
};

export type JSON_HideRoom = {
  roomId: string;
  activateRoomId: string;
};

export type ServerEvent_Settings = {
  helpUri?: string;
  permissionsHelpUri?: string;
  videoCallInfoTitle?: string;
  videoCallInfoContent?: string;
};

export type ServerEvent_Background = {
  roomId: string;
  uri: string;
  videoUri: string;
  hexColor: string;
  blur: number;
};

export type ServerEvent_BackgroundMusic = {
  trackUri: string;
  volume: number;
  loop: boolean;
};

export type ServerEvent_UserCursorData = {
  slug: string;
  lastCursorData: ServerEvent_UserCursor;
};

export type ServerEvent_UserCursor = {
  mousePosX: number;
  mousePosY: number;
  mouseClicked: boolean;
  text: string;
  showCursor: boolean;
};

export enum EventServerToClient {
  CONNECT = 'connect',
  DISCONNECT = 'disconect',
  ERROR = 'error',
  NEW_SESSION = 'new_session',
  OBJECTS = 'objects',
  ROOM_TERMINATED = 'room_terminated',
  ENTITIES_DISAPPEAR = 'entities_disappear',
  ROOM_UPDATE_USERS = 'room_update_users',

  BACKGROUND = 'background',
  BACKGROUNDMUSIC = 'background_music',
  SETTINGS = 'settings',
  ROOM_CURSORS = 'room_cursors',
  CLEAN_LOCAL_USER_TEXTS = 'clean_local_user_texts',
  CLEAR_CASHED_TOKEN = 'clear_cashed_token',
  VIDEO_SYNC_ACTION = 'video_sync_action',
  SERVER_STATUS = 'server_status',
  LOGGED_OUT = 'loggedout',
  BRAINSNACK = 'brainsnack',
  USER_INFO = 'user_logged_in',
  // USER_PROFILE = 'user_profile',
  TOKEN_EXPIRING_SOON = 'token_expiring_soon',
}

export const enum EventClientToServer {
  USER_EMIT = 0,
  READYCHECK_READY_TO_CONTINUE = 1,
  PAGED_TEXT_COMPLETED = 2,
  USER_PLACED_TEXT = 3,
  USER_CHANGED_TEXT = 4,
  UPDATE_OBJECT = 5,
  REMOVE_OBJECT = 6,
  TIMER_START = 7,
  QUESTION_READY_TO_CONTINUE = 8,
  REQUEST_ENTITY_UPDATE = 9,
  CLIENT_READY = 10,
  CHANGE_USER_DETAILS = 11,
  // START_MODULE_PART = 12, //depricated
  BRAINSNACK_READ = 13,
  // USER_ITEM_REMEMBER = 14, //deprecated
  OPEN_GROUP_ROOM = 15,
  OPEN_ROOM_NODE = 16,
  JOIN_LIVE_SESSION = 17,
  // OPEN_JOURNEY_NODE = 18, //depricated
  // OPEN_RESERVATION_NODE = 19, //depricated
  SET_VIDEOCALL_PREP_READY = 20,
  SET_CAPTUREOBJECT_PHOTO_UPLOADED = 21, //depricated
  REQUEST_NAVIGATION = 22,
  CLOSE_QUOTE = 23,
  SET_OBJECT_STATE = 24, //depricated
  VIDEOCALL_ACTION = 25,
  DROP_AREA_ATTACH_ENTITY = 26,
  DROP_AREA_DETACH_ENTITY = 27,
  BUTTON_CLICKED = 28,
  NAVIGATION_NODE_CLICKED = 30,
  VIDEOCALL_JOINED = 31,
  QUESTION_ANSWER = 32,
  VIDEO_SYNC_ACTION = 33,
  SERVER_DEBUG_COMMAND = 34,
  // LIST_SELECT = 35,
  // LIST_SEARCH = 36,
  LOBBY_CLIENT_READY = 37,
  INSTRUCTION__PREPARATION_DONE = 38,
  IMAGE_UPLOAD_RESPONSE = 39,
  // START_RESERVATION_NODE = 40,
  USER_LOGS_OUT = 41,
  // GET_USER_PROFILE = 42,
  // RESERVATION_REQUEST = 43,
  CMS_CONSOLE = 44,
  CHAT_MESSAGE = 45,
  CHAT_MESSAGE_READ = 46,
  RELOAD_USER_DATA = 47,
  SYNC_SOE_ITEM = 48,
  SYNC_RESILIENCE_CANVAS_PART = 49,
  SOE_ITEM_CANVAS_EDIT = 50,
  // JOURNEY = 51,
  BREAKOUT_CONTROLLER_ACTION = 52,
  JITSI_ERROR = 53,
  DEVICES_CHECKED = 54,
  JOURNEY_VIEW_PREPARE_START = 55,
  REMOVE_PROFILE_PIC = 56,
  SET_SUBTITLES_PREFERENCE = 57,
  SET_USER_FLAG = 58,
  UPDATE_ACCESS_TOKEN = 59,
  TEXT_INPUT_VALUE_CHANGE = 60,
  STEPCONTROLLER_CLICKED = 61,
  SUPPORT_WIDGET_OPENED = 62,
}

export enum SocketConnectErrorCode {
  AUTH_FAILED = 'AUTH_FAILED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  SERVER_UNAVAILABLE = 'SERVER_UNAVAILABLE',
}

export const EventClientToServer_to_string = [
  'USER_EMIT',
  'READYCHECK_READY_TO_CONTINUE',
  'PAGED_TEXT_COMPLETED',
  'USER_PLACED_TEXT',
  'USER_CHANGED_TEXT',
  'UPDATE_OBJECT',
  'REMOVE_OBJECT',
  'TIMER_START',
  'QUESTION_READY_TO_CONTINUE',
  'REQUEST_ENTITY_UPDATE',
  'CLIENT_READY',
  'CHANGE_USER_DETAILS',
  '',
  'BRAINSNACK_READ',
  '',
  'OPEN_GROUP_ROOM',
  'OPEN_ROOM_NODE',
  '',
  '',
  '',
  'SET_VIDEOCALL_PREP_READY',
  'SET_CAPTUREOBJECT_PHOTO_UPLOADED', //depricated
  'REQUEST_NAVIGATION',
  'CLOSE_QUOTE',
  'SET_OBJECT_STATE', //depricated
  'VIDEOCALL_ACTION',
  'DROP_AREA_ATTACH_ENTITY',
  'DROP_AREA_DETACH_ENTITY',
  'BUTTON_CLICKED',
  '',
  'NAVIGATION_NODE_CLICKED',
  'VIDEOCALL_JOINED',
  'QUESTION_ANSWER',
  'VIDEO_SYNC_ACTION',
  'SERVER_DEBUG_COMMAND',
  '',
  '',
  'LOBBY_CLIENT_READY',
  'INSTRUCTION__PREPARATION_DONE',
  'IMAGE_UPLOAD_RESPONSE',
  '',
  'USER_LOGS_OUT',
  'GET_USER_PROFILE',
  '',
  'CMS_CONSOLE',
  'CHAT_MESSAGE',
  'CHAT_MESSAGE_READ',
  'RELOAD_USER_DATA',
  '',
  'SYNC_RESILIENCE_CANVAS_PART',
  '',
  '',
  'BREAKOUT_CONTROLLER_ACTION',
  'JITSI_ERROR',
  'DEVICES_CHECKED',
  'JOURNEY_VIEW_PREPARE_START',
  'REMOVE_PROFILE_PIC',
  'SET_SUBTITLES_PREFERENCE',
  'SET_USER_FLAG',
  'UPDATE_ACCESS_TOKEN',
  'TEXT_INPUT_VALUE_CHANGE',
  'STEPCONTROLLER_CLICKED',
  'SUPPORT_WIDGET_OPENED',
];
